// src/pages/AdminPage.jsx
// Admin-Bereich: Nutzer-Verwaltung mit Rollen + Aktiv-Status.
// Passwort-Vergabe nur durch Admin via Supabase Dashboard.

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { C, zurueckStil } from './formui';
import { sicherungenLaden, sollMerken, sicherungLoeschen } from '../lib/ueberspannung';
import { datensicherungErstellen, datensicherungHerunterladen, letzteDatensicherung, protokollArchivErstellen } from '../lib/datensicherung';

// SQL fuer den Supabase-Editor: neues Passwort setzen und die E-Mail als
// bestaetigt markieren (sonst "Email not confirmed" beim Anmelden).
function passwortSql(email) {
  const e = String(email || '').replace(/'/g, "''");
  return "update auth.users\nset encrypted_password = extensions.crypt('NEUES-PASSWORT', extensions.gen_salt('bf')),\n    email_confirmed_at = coalesce(email_confirmed_at, now()),\n    updated_at = now()\nwhere email = '" + e + "';";
}


const ROLLEN = [
  { v: 'monteur', l: 'Monteur', icon: 'M', desc: 'Eigene & zugewiesene Protokolle', color: C.blue,   bg: C.blueBg },
  { v: 'buero',   l: 'Büro',   icon: 'B', desc: 'Alle Protokolle, Zuweisen, PDF',  color: C.orange, bg: C.orangeBg },
  { v: 'admin',   l: 'Admin',   icon: 'A', desc: 'Vollzugriff + Nutzerverwaltung',  color: C.green,  bg: C.greenBg },
];

const FILTER_OPTIONS = [
  { v: 'all',     l: 'Alle' },
  { v: 'monteur', l: 'Monteure' },
  { v: 'buero',   l: 'Büro' },
  { v: 'admin',   l: 'Admins' },
  { v: 'inactive',l: 'Deaktiviert' },
];


// Name eines Mitarbeiters, direkt in der Liste aenderbar. Steht noch die
// E-Mail als Name drin (so legt Supabase neue Nutzer an), wird sie ausgegraut
// als Platzhalter gezeigt - dann sieht man sofort, wo noch etwas fehlt.
function NameFeld({ wert, email, onSpeichern }) {
  const echterName = wert && !wert.includes('@') ? wert : '';
  const [text, setText] = useState(echterName);
  useEffect(() => { setText(wert && !wert.includes('@') ? wert : ''); }, [wert]);
  const sichern = () => { if (text.trim() !== echterName) onSpeichern(text); };
  return (
    <input
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={sichern}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      placeholder={'Vorname Nachname'}
      title={'Name für die Begrüßung in der App'}
      style={{
        fontWeight: 700, color: C.navy, fontSize: 16, padding: '5px 8px',
        border: '1.5px solid ' + (echterName ? 'transparent' : C.yellow),
        background: echterName ? 'transparent' : C.yellowBg,
        borderRadius: 7, minWidth: 170, maxWidth: 260, fontFamily: 'inherit',
      }}
      onFocus={e => { e.target.style.borderColor = C.navy; e.target.style.background = '#FFFFFF'; }}
    />
  );
}

// Rufname - nur ausfuellen, wenn jemand anders angesprochen werden will als
// sein Vorname lautet. Leer heisst: Vorname aus dem Namen nehmen.
function RufnameFeld({ wert, name, onSpeichern }) {
  const [text, setText] = useState(wert || '');
  useEffect(() => { setText(wert || ''); }, [wert]);
  const vorname = (name && !name.includes('@') ? name : '').split(' ')[0];
  const sichern = () => { if (text.trim() !== (wert || '')) onSpeichern(text); };
  return (
    <input
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={sichern}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      placeholder={vorname ? 'Anrede: ' + vorname : 'Anrede'}
      title={'Wie soll die App gruessen? Leer = Vorname'}
      style={{
        color: C.gray500, fontSize: 16, padding: '5px 8px',
        border: '1.5px solid ' + C.gray200, background: 'transparent',
        borderRadius: 7, width: 130, fontFamily: 'inherit',
      }}
    />
  );
}

export default function AdminPage({ onBack, onOpenVnb, onOpenPasswort }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showHelp, setShowHelp] = useState(false);
  const [showPasswordHelp, setShowPasswordHelp] = useState(null); // { email }
  const [msg, setMsg] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('user_profiles').select('*').order('name');
    if (error) {
      setMsg({ type: 'error', text: 'Fehler beim Laden: ' + error.message });
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  };

  const changeRole = async (userId, newRole) => {
    const { error } = await supabase.from('user_profiles').update({ role: newRole }).eq('id', userId);
    if (error) {
      setMsg({ type: 'error', text: 'Rollen-Änderung fehlgeschlagen: ' + error.message });
    } else {
      setMsg({ type: 'success', text: 'Rolle aktualisiert.' });
      loadUsers();
    }
  };

  // Der Name steht auf der Startseite in der Begruessung ("Guten Morgen,
  // Marcus"). Ohne gepflegten Namen traegt Supabase die E-Mail ein - deshalb
  // muss er hier aenderbar sein.
  const changeName = async (userId, neuerName) => {
    const { error } = await supabase.from('user_profiles')
      .update({ name: neuerName.trim() }).eq('id', userId);
    if (error) setMsg({ type: 'error', text: 'Name konnte nicht gespeichert werden: ' + error.message });
    else { setMsg({ type: 'success', text: 'Name gespeichert.' }); loadUsers(); }
  };

  // Rufname: wie jemand angesprochen werden will. Samira heisst bei allen
  // "Sascha" - dann steht das in der Begruessung, der richtige Name aber
  // weiterhin im Protokoll.
  const changeRufname = async (userId, neuerRufname) => {
    const wert = neuerRufname.trim();
    const { error } = await supabase.from('user_profiles')
      .update({ rufname: wert || null }).eq('id', userId);
    if (error) setMsg({ type: 'error', text: 'Rufname konnte nicht gespeichert werden: ' + error.message });
    else { setMsg({ type: 'success', text: 'Rufname gespeichert.' }); loadUsers(); }
  };

  const toggleActive = async (userId, currentActive) => {
    const newActive = !currentActive;
    const verb = newActive ? 'reaktivieren' : 'deaktivieren';
    if (!window.confirm('Nutzer wirklich ' + verb + '?')) return;
    const { error } = await supabase.from('user_profiles').update({ active: newActive }).eq('id', userId);
    if (error) {
      setMsg({ type: 'error', text: 'Änderung fehlgeschlagen: ' + error.message });
    } else {
      setMsg({ type: 'success', text: newActive ? 'Nutzer reaktiviert.' : 'Nutzer deaktiviert.' });
      loadUsers();
    }
  };

  const filteredUsers = users.filter(u => {
    if (filter === 'inactive' && u.active !== false) return false;
    if (filter !== 'all' && filter !== 'inactive' && u.role !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(u.name || '').toLowerCase().includes(s) && !(u.email || '').toLowerCase().includes(s)) return false;
    }
    return true;
  });

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
      <div style={{ background: C.navy, padding: '0 16px', boxShadow: '0 2px 14px rgba(0,0,0,0.3)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={onBack} style={zurueckStil}>‹ Zurück</button>
            <div>
              <div style={{ color: C.white, fontWeight: 800, fontSize: 16 }}>Nutzerverwaltung</div>
              <div style={{ color: C.onDark, fontSize: 13 }}>Admin-Bereich</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Admins kamen ueber "Mehr" nur hierher - Passwort und Sprache
                waren fuer sie nicht mehr erreichbar. */}
            {onOpenPasswort && (
              <button onClick={onOpenPasswort} style={{ background: C.navyMid, color: C.white, border: '1px solid ' + C.navyLight, borderRadius: 9, padding: '10px 16px', fontWeight: 700, fontSize: 15, cursor: 'pointer', minHeight: 44 }}>Passwort &amp; Sprache</button>
            )}
            {onOpenVnb && (
              <button onClick={onOpenVnb} style={{ background: C.navyMid, color: C.yellow, border: '1px solid ' + C.navyLight, borderRadius: 9, padding: '10px 16px', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>VNBs verwalten</button>
            )}
            <button onClick={() => setShowHelp(true)} style={{ background: C.yellow, color: C.navy, border: 'none', borderRadius: 9, padding: '10px 18px', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>+ Neuen Nutzer anlegen</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {ROLLEN.map(r => (
            <div key={r.v} style={{ background: C.white, borderRadius: 10, padding: '12px 14px', border: '1.5px solid ' + r.bg }}>
              <div style={{ background: r.bg, color: r.color, display: 'inline-block', borderRadius: 12, padding: '2px 10px', fontSize: 13, fontWeight: 700, marginBottom: 5 }}>{r.l}</div>
              <div style={{ fontSize: 14, color: C.gray500 }}>{r.desc}</div>
            </div>
          ))}
        </div>

        {msg && (
          <div style={{ background: msg.type === 'success' ? C.greenBg : C.redBg, border: '1px solid ' + (msg.type === 'success' ? C.green : C.red), borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 15, color: msg.type === 'success' ? C.green : C.red, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{msg.text}</span>
            <button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }}>x</button>
          </div>
        )}

        <Datensicherung/>
        <ProtokollArchiv/>
        <UeberspannungListe/>

        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <input type='text' value={search} onChange={e => setSearch(e.target.value)} placeholder='Suchen: Name oder E-Mail...'
            style={{ flex: 1, minWidth: 200, padding: '10px 14px', borderRadius: 8, border: '1.5px solid ' + C.gray200, fontSize: 16, outline: 'none', boxSizing: 'border-box', background: C.white }}/>
          <select value={filter} onChange={e => setFilter(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid ' + C.gray200, fontSize: 16, background: C.white, cursor: 'pointer' }}>
            {FILTER_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>

        <div style={{ background: C.white, borderRadius: 12, boxShadow: '0 1px 8px rgba(10,22,40,0.08)', overflow: 'hidden' }}>
          <div style={{ background: C.navyMid, padding: '12px 16px' }}>
            <div style={{ color: C.white, fontWeight: 700, fontSize: 14 }}>Nutzer ({filteredUsers.length}{filteredUsers.length !== users.length && ' von ' + users.length})</div>
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.gray500 }}>Lädt …</div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.gray500 }}>Keine Nutzer gefunden{search ? ' für "' + search + '"' : ''}.</div>
          ) : (
            filteredUsers.map((u, i) => {
              const rolle = ROLLEN.find(r => r.v === u.role) || ROLLEN[0];
              const isCurrentUser = u.id === user?.id;
              const isInactive = u.active === false;
              return (
                <div key={u.id} style={{ padding: '14px 16px', borderBottom: i < filteredUsers.length - 1 ? '1px solid ' + C.gray100 : 'none', display: 'flex', alignItems: 'center', gap: 12, opacity: isInactive ? 0.55 : 1 }}>
                  <div style={{ width: 42, height: 42, background: rolle.bg, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: rolle.color, fontSize: 14, flexShrink: 0 }}>{rolle.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <NameFeld wert={u.name} email={u.email} onSpeichern={(n) => changeName(u.id, n)} />
                      <RufnameFeld wert={u.rufname} name={u.name} onSpeichern={(n) => changeRufname(u.id, n)} />
                      {isCurrentUser && <span style={{ fontSize: 13, color: C.gray500 }}>(Du)</span>}
                      {isInactive && <span style={{ fontSize: 12, fontWeight: 700, color: C.red, background: C.redBg, padding: '2px 6px', borderRadius: 4 }}>DEAKTIVIERT</span>}
                    </div>
                    <div style={{ fontSize: 14, color: C.gray500 }}>{u.email}</div>
                  </div>
                  <select value={u.role} onChange={e => changeRole(u.id, e.target.value)} disabled={isCurrentUser}
                    style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid ' + rolle.bg, background: rolle.bg, color: rolle.color, fontWeight: 700, fontSize: 14, cursor: isCurrentUser ? 'not-allowed' : 'pointer' }}>
                    {ROLLEN.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
                  </select>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setShowPasswordHelp({ email: u.email, name: u.name || u.email })} title='Passwort setzen'
                      style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid ' + C.gray200, background: 'none', cursor: 'pointer', fontSize: 14, color: C.gray500, fontWeight: 600 }}>Passwort...</button>
                    {!isCurrentUser && (
                      <button onClick={() => toggleActive(u.id, u.active !== false)}
                        style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid ' + (isInactive ? C.green : C.red), background: isInactive ? C.greenBg : C.redBg, cursor: 'pointer', fontSize: 14, color: isInactive ? C.green : C.red, fontWeight: 600 }}>
                        {isInactive ? 'Reaktivieren' : 'Deaktivieren'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showHelp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: C.white, borderRadius: 14, padding: 28, maxWidth: 560, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: C.navy, marginBottom: 16 }}>Neuen Nutzer anlegen</div>
            <div style={{ fontSize: 15, color: C.gray500, marginBottom: 16, lineHeight: 1.6 }}>
              Aus Sicherheitsgruenden koennen neue Auth-Nutzer nicht direkt aus der App erstellt werden. Du legst sie schnell in Supabase an, dann kannst du hier die Rolle setzen.
            </div>
            <div style={{ background: C.gray100, borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: C.navy, fontSize: 15, marginBottom: 8 }}>Schritt für Schritt:</div>
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 15, color: C.gray700, lineHeight: 1.8 }}>
                <li>Gehe zu <strong>app.supabase.com</strong> und öffne dein Projekt</li>
                <li>Links: <strong>Authentication</strong> (Personen-Icon)</li>
                <li>Oben rechts: <strong>"Add user" - "Create new user"</strong></li>
                <li>E-Mail eingeben (z.B. <code>monteur1@etb-karlsruhe.de</code>)</li>
                <li>Passwort setzen (z.B. <code>etb2025-Vorname!</code>)</li>
                <li><strong>Auto Confirm User</strong> Haekchen setzen!</li>
                <li><strong>"Create user"</strong> klicken</li>
                <li>Zurück in diese App, Tab wechseln und wieder zurück</li>
                <li>Der neue Nutzer erscheint mit Rolle <strong>Monteur</strong></li>
                <li>Hier in der App: Rolle anpassen falls nötig</li>
              </ol>
            </div>
            <div style={{ background: C.yellowBg, borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 14, color: C.navy, lineHeight: 1.5 }}>
              <strong>Wichtig:</strong> Gib dem Mitarbeiter E-Mail und Passwort persoenlich weiter. Du als Admin verwaltest alle Passwoerter.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowHelp(false)} style={{ padding: '12px 24px', borderRadius: 9, background: C.yellow, color: C.navy, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>Verstanden</button>
            </div>
          </div>
        </div>
      )}

      {showPasswordHelp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: C.white, borderRadius: 14, padding: 28, maxWidth: 540, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: C.navy, marginBottom: 8 }}>Passwort für {showPasswordHelp.name}</div>
            <div style={{ fontSize: 14, color: C.gray500, marginBottom: 16 }}>{showPasswordHelp.email}</div>
            <div style={{ background: C.gray100, borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: C.navy, fontSize: 15, marginBottom: 8 }}>So setzt du ein neues Passwort:</div>
              {/* Stand 15.09.2026: Supabase hat im Nutzer-Fenster KEIN Feld mehr,
                  um ein Passwort direkt zu setzen (nur "Send password recovery"
                  per Mail). Deshalb ueber den SQL Editor. */}
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 15, color: C.gray700, lineHeight: 1.8 }}>
                <li>Unten auf <strong>„Text kopieren"</strong> tippen</li>
                <li>In Supabase links den <strong>SQL Editor</strong> (Zeichen <code>&gt;_</code>) öffnen, <strong>„+ New query"</strong></li>
                <li>Einfügen (Strg+V) und <code>NEUES-PASSWORT</code> durch das neue Passwort ersetzen (mind. 8 Zeichen, die Hochkommas <code>'</code> bleiben stehen)</li>
                <li><strong>„Run"</strong> klicken – es erscheint „Success"</li>
                <li>Die Abfrage ohne Speichern schließen und dem Mitarbeiter das Passwort persönlich mitteilen</li>
              </ol>
              <pre style={{ margin: '12px 0 8px', padding: 10, background: C.white, border: '1px solid ' + C.gray200, borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{passwortSql(showPasswordHelp.email)}</pre>
              <button type="button"
                onClick={() => { try { navigator.clipboard.writeText(passwortSql(showPasswordHelp.email)); } catch { /* dann von Hand markieren */ } }}
                style={{ minHeight: 44, padding: '8px 16px', borderRadius: 8, border: '1.5px solid ' + C.navy, background: C.white, color: C.navy, fontWeight: 700, cursor: 'pointer' }}>
                Text kopieren
              </button>
            </div>
            <div style={{ background: C.blueBg, borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 14, color: C.navy, lineHeight: 1.5 }}>
              <strong>Hinweis:</strong> Da Mitarbeiter selbst kein Passwort ändern duerfen, muss der Reset immer über dich als Admin laufen. Verlangt der Mitarbeiter das Passwort, setzt du ein neues und gibst es weiter.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowPasswordHelp(null)} style={{ padding: '12px 24px', borderRadius: 9, background: C.yellow, color: C.navy, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Datensicherung der ganzen Datenbank als Datei (18.09.2026). Supabase Free-Tier
// sichert nicht selbst - also regelmaessig hier herunterladen.
function Datensicherung() {
  const [laeuft, setLaeuft] = useState(false);
  const [stand, setStand] = useState(null);
  const [meldung, setMeldung] = useState(null);
  const [letzte, setLetzte] = useState(letzteDatensicherung());
  const starten = async () => {
    setLaeuft(true); setMeldung(null);
    try {
      const erg = await datensicherungErstellen((name, n, gesamt) => setStand({ name, n, gesamt }));
      const { name, groesse } = datensicherungHerunterladen(erg);
      const zeilen = Object.values(erg.anzahl).reduce((a, b) => a + b, 0);
      const fehler = Object.keys(erg.fehler);
      setMeldung({ ok: fehler.length === 0,
        text: 'Gespeichert als „' + name + '“ (' + Math.round(groesse / 1024) + ' KB, ' + zeilen.toLocaleString('de-DE') + ' Einträge aus '
          + Object.keys(erg.anzahl).length + ' Tabellen).'
          + (fehler.length ? ' Nicht gesichert: ' + fehler.map(f => f + ' (' + erg.fehler[f] + ')').join('; ') : '') });
      setLetzte(erg.erstellt);
    } catch (e) {
      setMeldung({ ok: false, text: 'Sicherung fehlgeschlagen: ' + (e.message || e) });
    } finally {
      setLaeuft(false); setStand(null);
    }
  };
  const letzteText = letzte ? new Date(letzte).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : 'auf diesem Gerät noch nie';
  return (
    <div style={{ background: C.white, borderRadius: 12, padding: '14px 16px', marginBottom: 20, boxShadow: '0 1px 8px rgba(10,22,40,0.08)',
                  display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 320px' }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 15 }}>Datensicherung</div>
        <div style={{ fontSize: 14, color: C.gray700, lineHeight: 1.45 }}>
          Lädt alle Tabellen (Kunden, Stationen, Protokolle, Mängel, Verträge …) als eine Datei auf diesen PC.
          Fotos und Vertrags-PDFs sind nicht enthalten. Letzte Sicherung: <strong>{letzteText}</strong>
        </div>
        {laeuft && stand && (
          <div style={{ fontSize: 13, color: C.gray500, marginTop: 4 }}>Lese {stand.name} … ({stand.n + 1} von {stand.gesamt})</div>
        )}
        {meldung && (
          <div style={{ fontSize: 14, marginTop: 6, color: meldung.ok ? C.green : C.red, fontWeight: 600 }}>{meldung.text}</div>
        )}
      </div>
      <button type='button' onClick={starten} disabled={laeuft}
        style={{ background: laeuft ? C.gray300 : C.navy, color: C.white, border: 'none', borderRadius: 8, padding: '10px 18px',
                 fontWeight: 700, fontSize: 14, cursor: laeuft ? 'wait' : 'pointer', minHeight: 44 }}>
        {laeuft ? 'Sicherung läuft …' : 'Datensicherung herunterladen'}
      </button>
    </div>
  );
}

// Monats-Archiv: alle FREIGEGEBENEN Protokolle eines Monats als Vollprotokoll-PDF
// in einer ZIP (Anna-Lena 18.09.2026). Standard: der Vormonat.
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
function ProtokollArchiv() {
  const vormonat = new Date(); vormonat.setDate(1); vormonat.setMonth(vormonat.getMonth() - 1);
  const [jahr, setJahr] = useState(vormonat.getFullYear());
  const [monat, setMonat] = useState(vormonat.getMonth() + 1);
  const [laeuft, setLaeuft] = useState(false);
  const [stand, setStand] = useState(null);
  const [meldung, setMeldung] = useState(null);
  const jahre = [];
  for (let j = new Date().getFullYear(); j >= 2025; j--) jahre.push(j);
  const starten = async () => {
    setLaeuft(true); setMeldung(null);
    try {
      const erg = await protokollArchivErstellen(jahr, monat, (n, gesamt, text) => setStand({ n, gesamt, text }));
      if (erg.anzahl === 0 && erg.fehler.length === 0) {
        setMeldung({ ok: true, text: 'Im ' + MONATE[monat - 1] + ' ' + jahr + ' gibt es keine freigegebenen Protokolle.' });
      } else {
        setMeldung({ ok: erg.fehler.length === 0,
          text: 'Gespeichert als „' + erg.dateiname + '“: ' + erg.anzahl + ' Protokoll(e), ' + Math.round(erg.groesse / 1024 / 1024 * 10) / 10 + ' MB.'
            + (erg.fehler.length ? ' ' + erg.fehler.length + ' konnten nicht erzeugt werden – siehe FEHLER.txt in der ZIP.' : '') });
      }
    } catch (e) {
      setMeldung({ ok: false, text: 'Archiv fehlgeschlagen: ' + (e.message || e) });
    } finally {
      setLaeuft(false); setStand(null);
    }
  };
  const feld = { padding: '9px 10px', borderRadius: 8, border: '1.5px solid ' + C.gray200, fontSize: 15, background: C.white, minHeight: 44 };
  return (
    <div style={{ background: C.white, borderRadius: 12, padding: '14px 16px', marginBottom: 20, boxShadow: '0 1px 8px rgba(10,22,40,0.08)',
                  display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 320px' }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 15 }}>Protokolle als PDF sichern</div>
        <div style={{ fontSize: 14, color: C.gray700, lineHeight: 1.45 }}>
          Alle vom Büro freigegebenen Protokolle eines Monats (nach Wartungsdatum) als Vollprotokoll-PDF in einer ZIP-Datei.
        </div>
        {laeuft && stand && (
          <div style={{ fontSize: 13, color: C.gray500, marginTop: 4 }}>
            {stand.n < stand.gesamt ? 'Erzeuge PDF ' + (stand.n + 1) + ' von ' + stand.gesamt + (stand.text ? ' – ' + stand.text : '') + ' …' : stand.text + ' …'}
          </div>
        )}
        {meldung && (
          <div style={{ fontSize: 14, marginTop: 6, color: meldung.ok ? C.green : C.red, fontWeight: 600 }}>{meldung.text}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={monat} onChange={e => setMonat(Number(e.target.value))} disabled={laeuft} style={feld} aria-label='Monat'>
          {MONATE.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select value={jahr} onChange={e => setJahr(Number(e.target.value))} disabled={laeuft} style={feld} aria-label='Jahr'>
          {jahre.map(j => <option key={j} value={j}>{j}</option>)}
        </select>
        <button type='button' onClick={starten} disabled={laeuft}
          style={{ background: laeuft ? C.gray300 : C.navy, color: C.white, border: 'none', borderRadius: 8, padding: '10px 18px',
                   fontWeight: 700, fontSize: 14, cursor: laeuft ? 'wait' : 'pointer', minHeight: 44 }}>
          {laeuft ? 'Archiv wird erstellt …' : 'ZIP herunterladen'}
        </button>
      </div>
    </div>
  );
}

// Ueberspannungsschutz: Hersteller / Typ -> Soll-Sicherung (Migration 69). Die App
// traegt den Soll-Wert im Protokoll (NSHV, Absicherung des Ueberspannungsschutzes)
// automatisch ein. Neue Typen entstehen auch, wenn das Buero im Protokoll einen
// Soll-Wert eintraegt.
function UeberspannungListe() {
  const [offen, setOffen] = useState(false);
  const [liste, setListe] = useState(null);
  const [neu, setNeu] = useState({ hersteller: '', typ: '', soll: '' });
  const [meldung, setMeldung] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const laden = async () => {
    try { setListe(await sicherungenLaden(true)); } catch (e) { setMeldung('Laden fehlgeschlagen: ' + (e.message || e)); }
  };
  useEffect(() => { if (offen && liste === null) laden(); }, [offen]); // eslint-disable-line react-hooks/exhaustive-deps
  const speichern = async (h, t, s) => {
    setLaeuft(true); setMeldung(null);
    try { await sollMerken(h, t, s); await laden(); return true; }
    catch (e) { setMeldung('Speichern fehlgeschlagen: ' + (e.message || e)); return false; }
    finally { setLaeuft(false); }
  };
  const loeschen = async (e) => {
    if (!window.confirm('„' + e.hersteller + ' ' + e.typ + '“ aus der Liste löschen?')) return;
    setLaeuft(true);
    try { await sicherungLoeschen(e.id); await laden(); }
    catch (err) { setMeldung('Löschen fehlgeschlagen: ' + (err.message || err)); }
    finally { setLaeuft(false); }
  };
  const feld = { padding: '8px 10px', borderRadius: 8, border: '1.5px solid ' + C.gray200, fontSize: 15, boxSizing: 'border-box' };
  return (
    <div style={{ background: C.white, borderRadius: 12, padding: '14px 16px', marginBottom: 20, boxShadow: '0 1px 8px rgba(10,22,40,0.08)' }}>
      <button type='button' onClick={() => setOffen(o => !o)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', width: '100%' }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 15 }}>{offen ? '▾' : '▸'} Überspannungsschutz – Sicherung laut Hersteller</div>
        <div style={{ fontSize: 14, color: C.gray700, lineHeight: 1.45 }}>
          Hersteller + Typ → Soll-Sicherung. Die App trägt den Soll-Wert im Protokoll (NSHV) automatisch ein und bewertet: nur Ist = Soll ist i.O.
        </div>
      </button>
      {offen && (
        <div style={{ marginTop: 10 }}>
          {meldung && <div style={{ color: C.red, fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{meldung}</div>}
          <form onSubmit={async ev => { ev.preventDefault(); if (await speichern(neu.hersteller, neu.typ, neu.soll)) setNeu({ hersteller: '', typ: '', soll: '' }); }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <input style={{ ...feld, flex: '1 1 150px' }} placeholder='Hersteller' value={neu.hersteller} onChange={e => setNeu({ ...neu, hersteller: e.target.value })}/>
            <input style={{ ...feld, flex: '1 1 150px' }} placeholder='Typ' value={neu.typ} onChange={e => setNeu({ ...neu, typ: e.target.value })}/>
            <input style={{ ...feld, width: 110 }} placeholder='Soll [A]' inputMode='decimal' value={neu.soll} onChange={e => setNeu({ ...neu, soll: e.target.value })}/>
            <button type='submit' disabled={laeuft || !neu.hersteller.trim() || !neu.typ.trim() || !neu.soll}
              style={{ background: C.navy, color: C.white, border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer', minHeight: 40 }}>
              Hinzufügen
            </button>
          </form>
          {liste === null ? <div style={{ fontSize: 14, color: C.gray500 }}>Lädt …</div>
            : liste.length === 0 ? <div style={{ fontSize: 14, color: C.gray500 }}>Noch keine Einträge.</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr style={{ textAlign: 'left', color: C.gray500 }}>
                  <th style={{ padding: '4px 6px' }}>Hersteller</th><th style={{ padding: '4px 6px' }}>Typ</th>
                  <th style={{ padding: '4px 6px' }}>Soll [A]</th><th/>
                </tr></thead>
                <tbody>
                  {liste.map(e => (
                    <tr key={e.id} style={{ borderTop: '1px solid ' + C.gray100 }}>
                      <td style={{ padding: '6px' }}>{e.hersteller}</td>
                      <td style={{ padding: '6px' }}>{e.typ}</td>
                      <td style={{ padding: '6px' }}>
                        <input key={e.soll_a} defaultValue={String(e.soll_a).replace('.', ',')} inputMode='decimal' disabled={laeuft}
                          style={{ ...feld, width: 80, padding: '4px 8px' }}
                          onBlur={ev => { if (ev.target.value && ev.target.value !== String(e.soll_a).replace('.', ',')) speichern(e.hersteller, e.typ, ev.target.value); }}/>
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>
                        <button type='button' onClick={() => loeschen(e)} disabled={laeuft}
                          style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 14, textDecoration: 'underline' }}>löschen</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}
    </div>
  );
}
